import React, { useState } from 'react';
import '../styles/Authentication.css';
import logo from '../images/pdr-logo.png';
import loadingGif from '../images/Loading_2.gif';
import eventTracker from '../services/EventTracker';

const Authentication = () => {
  const [logoError, setLogoError] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');

 // const PIPEDREAM_LOGIN_ENDPOINT = 'https://eoq40z7ynvpfp0s.m.pipedream.net';  // OLD
  const PIPEDREAM_LOGIN_ENDPOINT = 'https://eotx3qbi2qyotu1.m.pipedream.net/api/UserSetup/Login';  // NEW

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'email') {
      const lowerEmail = value.toLowerCase();
      setFormData(prev => ({
        ...prev,
        [name]: lowerEmail
      }));

      if (lowerEmail && !lowerEmail.endsWith('@pacificdebt.com') && lowerEmail.includes('@')) {
        setEmailError('Please use your @pacificdebt.com email address');
      } else {
        setEmailError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (message) {
      setMessage('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (!formData.email.endsWith('@pacificdebt.com')) {
      newErrors.email = 'Please use your @pacificdebt.com email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        action: 'login'
      };

      const response = await fetch(PIPEDREAM_LOGIN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Login successful! Redirecting...');
        setEmailError('');

        localStorage.setItem('user', JSON.stringify(data.user));

        eventTracker.trackLogin('standard', {
          loginSuccess: true,
          userRole: data.user.role,
          loginAttemptTime: new Date().toISOString(),
          userAgent: navigator.userAgent,
          loginPage: window.location.pathname,
          redirectTarget: '/pdr-coreapp3/dashboard'
        });

        eventTracker.trackFormSubmission('login_form', true, {
          formLocation: 'authentication_page',
          submissionTime: new Date().toISOString(),
          userEmail: formData.email,
          userRole: data.user.role
        });

        setTimeout(() => {
          window.location.href = '/pdr-coreapp3/dashboard';
        }, 1500);
      } else {
        setMessage('Username or password is incorrect');

        eventTracker.trackError('Login failed', `HTTP ${response.status}`, {
          loginAttempt: true,
          credentials: { email: formData.email },
          failureReason: data.message || response.statusText,
          errorCode: response.status
        });

        eventTracker.trackFormSubmission('login_form', false, {
          formLocation: 'authentication_page',
          submissionTime: new Date().toISOString(),
          userEmail: formData.email,
          errorMessage: 'Username or password is incorrect'
        });
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setMessage('Network error. Please check your connection and try again.');

      eventTracker.trackError('Login network error', error.stack, {
        loginAttempt: true,
        errorType: 'network_error',
        userEmail: formData.email,
        endpoint: PIPEDREAM_LOGIN_ENDPOINT
      });

      eventTracker.trackFormSubmission('login_form', false, {
        formLocation: 'authentication_page',
        submissionTime: new Date().toISOString(),
        userEmail: formData.email,
        errorMessage: 'Network error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    eventTracker.trackButtonClick('forgot_password', {
      buttonLocation: 'authentication_page',
      userEmail: formData.email,
      redirectTarget: 'https://servicedesk.pacificdebt.com/hc/en-us/requests/new'
    });

    window.location.href = 'https://servicedesk.pacificdebt.com/hc/en-us/requests/new';
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="logo-container">
            {!logoError ? (
              <img
                src={logo}
                alt="Pacific Debt Relief"
                className="auth-logo"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="auth-logo-text">
                <span className="logo-icon">PD</span>
                <span className="logo-text">Pacific Debt Relief</span>
              </div>
            )}
          </div>
          <h1 className="app-title">PDR Core Web App</h1>
          <h2 className="login-title">Login</h2>
          <p className="auth-subtitle">Enter your email and password for this app</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email || emailError || message ? 'error' : ''}
              placeholder="username@pacificdebt.com"
              pattern="[a-z0-9._%+-]+@pacificdebt\.com$"
              title="Please use your @pacificdebt.com email address"
              autoComplete="email"
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
            {!errors.email && emailError && <span className="error-text">{emailError}</span>}
          </div>

          <div className="form-group">
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={errors.password || message ? 'error' : ''}
              placeholder="••••••••"
              autoComplete="current-password"
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>

          {message && (
            <div className={`auth-message ${message.includes('successful') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <img
                src={loadingGif}
                alt="Loading..."
                className="loading-icon"
              />
            ) : (
              'Login'
            )}
          </button>

          <button
            type="button"
            className="forgot-password-link"
            onClick={handleForgotPassword}
            disabled={isLoading}
          >
            Forgot password
          </button>
        </form>
      </div>
    </div>
  );
};

export default Authentication;